import { useCallback, useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import {
  DEFAULT_SITE_SETTINGS,
  loadSiteSettings,
  saveSiteSettings,
  type SiteSettings,
} from '@/services/siteSettings';
import { SiteSettingsContext, type SiteSettingsContextValue } from './useSiteSettings';

function applyBrowserMetadata(settings: SiteSettings) {
  if (typeof document === 'undefined') return;

  if (settings.general.storeName) {
    document.title = settings.general.storeName;
  }

  if (settings.general.faviconUrl) {
    let favicon = document.querySelector<HTMLLinkElement>('link[rel="icon"]');
    if (!favicon) {
      favicon = document.createElement('link');
      favicon.rel = 'icon';
      document.head.appendChild(favicon);
    }
    favicon.href = settings.general.faviconUrl;
  }
}

export function SiteSettingsProvider({ children }: { children: ReactNode }) {
  const [settings, setSettings] = useState<SiteSettings>(DEFAULT_SITE_SETTINGS);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const refreshSettings = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const next = await loadSiteSettings();
      setSettings(next);
      applyBrowserMetadata(next);
      return next;
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Unable to load site settings.';
      setError(message);
      return DEFAULT_SITE_SETTINGS;
    } finally {
      setLoading(false);
    }
  }, []);

  const updateSettings = useCallback(async (nextSettings: SiteSettings) => {
    setError('');
    const saved = await saveSiteSettings(nextSettings);
    setSettings(saved);
    applyBrowserMetadata(saved);
    return saved;
  }, []);

  useEffect(() => {
    refreshSettings();
  }, [refreshSettings]);

  const value = useMemo<SiteSettingsContextValue>(() => ({
    settings,
    loading,
    error,
    refreshSettings,
    updateSettings,
  }), [error, loading, refreshSettings, settings, updateSettings]);

  return (
    <SiteSettingsContext.Provider value={value}>
      {children}
    </SiteSettingsContext.Provider>
  );
}
